import { useState } from "react";
import { MDBBadge, MDBIcon } from "mdbreact";

const lastDayOf = (day) =>
  new Date(day).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

// what a demo branch is asked to do, shown under the label
const POINTS = [
  {
    icon: "check-circle",
    color: "#2e7d32",
    title: "Explore & Test",
    text: "Try the available laboratory workflows and features.",
  },
  {
    icon: "hand-holding-heart",
    color: "#ef6c00",
    title: "Use It Properly",
    text: "Please use the system with care and encode your records accurately.",
  },
  {
    icon: "comment",
    color: "#2962ff",
    title: "Share Your Feedback",
    text: "Suggest missing features, improvements, or report issues.",
  },
];

/**
 * Label of a demo branch, centered on the top bar: that it is a demo and its
 * last day. Hovering it shows what the demo is for.
 * `demo` is { end, isOver, daysLeft } (services/utilities/demo).
 */
export default function DemoNotice({ demo }) {
  const [isHovered, setIsHovered] = useState(false);

  if (!demo?.end) return null;

  const remaining = demo.isOver
    ? "ended"
    : demo.daysLeft
      ? `${demo.daysLeft} day(s) left`
      : "ends today";

  return (
    <div
      className="d-none d-md-block"
      style={{
        position: "absolute",
        left: "50%",
        transform: "translateX(-50%)",
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <MDBBadge
        className="py-2 px-3"
        color={demo.isOver || demo.daysLeft <= 7 ? "danger" : "info"}
        style={{
          whiteSpace: "nowrap",
          fontSize: "1rem",
          fontWeight: 400,
          boxShadow: "0px 0px 0px 0px",
          cursor: "default",
        }}
      >
        <MDBIcon icon="clock" className="mr-1" />
        For demo purposes only · last day {lastDayOf(demo.end)} · {remaining}
      </MDBBadge>

      {isHovered && (
        // starts right under the label, so the pointer can move onto it
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: "50%",
            transform: "translateX(-50%)",
            paddingTop: 8,
            width: 440,
            maxWidth: "90vw",
            zIndex: 1000,
          }}
        >
          <div
            className="rounded z-depth-2"
            style={{ background: "#16224f", padding: 10 }}
          >
            <div className="d-flex justify-content-between align-items-center px-2 pb-2">
              <b className="white-text">
                <MDBIcon icon="flask" className="mr-2" />
                Pinoy iMD
              </b>
              <MDBBadge
                style={{ background: "#3b2530", color: "#ff7043" }}
                className="z-depth-0"
              >
                DEMO
              </MDBBadge>
            </div>
            <div className="bg-white rounded p-3" style={{ color: "#212529" }}>
              <b>
                <MDBIcon far icon="lightbulb" className="mr-2 text-primary" />
                Try Pinoy iMD Demo
              </b>
              <p className="mt-2 mb-2">
                Explore the system, test its features, and help us make it
                better!
              </p>
              <hr className="my-2" />
              {POINTS.map(({ icon, color, title, text }) => (
                <div key={title} className="d-flex mb-2">
                  <MDBIcon
                    far={icon !== "hand-holding-heart"}
                    icon={icon}
                    className="mr-2 mt-1"
                    style={{ color, width: 18 }}
                  />
                  <div>
                    <b>{title}</b>
                    <small className="d-block text-muted">{text}</small>
                  </div>
                </div>
              ))}
              <small className="d-block text-primary font-weight-bold">
                Your feedback helps shape the future of Pinoy iMD.
              </small>
              <small className="d-block text-muted mt-2">
                <MDBIcon far icon="clock" className="mr-1" />
                This demo can be used up to <b>{lastDayOf(demo.end)}</b> (
                {remaining}). Contact us to continue after that.
              </small>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
